import type { RaceAnswerOutcome } from './raceRules';

export type RacerKey = 'player' | 'botOne' | 'botTwo';

export type RacerMotion = {
  distance: number;
  boostSpeed: number;
  boostRemainingMs: number;
  lane: number;
};

export type RaceMotionState = {
  elapsedMs: number;
  racers: Record<RacerKey, RacerMotion>;
};

export const RACE_CRUISE_SPEED = 92;
export const RACE_BOOST_DURATION_MS = 1_100;

export function initialRaceMotionState(): RaceMotionState {
  return {
    elapsedMs: 0,
    racers: {
      player: { distance: 1_000, boostSpeed: 0, boostRemainingMs: 0, lane: 0 },
      botOne: { distance: 1_032, boostSpeed: 0, boostRemainingMs: 0, lane: -0.72 },
      botTwo: { distance: 968, boostSpeed: 0, boostRemainingMs: 0, lane: 0.72 },
    },
  };
}

function advanceRacer(racer: RacerMotion, deltaMs: number): RacerMotion {
  const activeBoostMs = Math.min(deltaMs, racer.boostRemainingMs);
  const distance = racer.distance
    + RACE_CRUISE_SPEED * deltaMs / 1_000
    + racer.boostSpeed * activeBoostMs / 1_000;
  const boostRemainingMs = Math.max(0, racer.boostRemainingMs - deltaMs);
  return {
    ...racer,
    distance,
    boostRemainingMs,
    boostSpeed: boostRemainingMs === 0 ? 0 : racer.boostSpeed,
  };
}

export function advanceRaceMotion(state: RaceMotionState, deltaMs: number): RaceMotionState {
  const safeDeltaMs = Math.max(0, Math.min(30_000, deltaMs));
  return {
    elapsedMs: state.elapsedMs + safeDeltaMs,
    racers: {
      player: advanceRacer(state.racers.player, safeDeltaMs),
      botOne: advanceRacer(state.racers.botOne, safeDeltaMs),
      botTwo: advanceRacer(state.racers.botTwo, safeDeltaMs),
    },
  };
}

function withBoost(racer: RacerMotion, boostSpeed: number): RacerMotion {
  return { ...racer, boostSpeed, boostRemainingMs: RACE_BOOST_DURATION_MS };
}

export function applyRaceMotionOutcome(state: RaceMotionState, outcome: RaceAnswerOutcome): RaceMotionState {
  if (outcome === 'correct') {
    return {
      ...state,
      racers: {
        ...state.racers,
        player: withBoost(state.racers.player, 92),
      },
    };
  }

  const botBoost = outcome === 'timeout' ? 88 : 64;
  return {
    ...state,
    racers: {
      ...state.racers,
      botOne: withBoost(state.racers.botOne, botBoost),
      botTwo: withBoost(state.racers.botTwo, botBoost),
    },
  };
}

export function raceMotionPlacement(state: RaceMotionState) {
  const playerDistance = state.racers.player.distance;
  const gaps: [number, number] = [
    Math.round(playerDistance - state.racers.botOne.distance),
    Math.round(playerDistance - state.racers.botTwo.distance),
  ];
  const place = (1 + gaps.filter((gap) => gap < 0).length) as 1 | 2 | 3;
  return { place, gaps };
}

export function racerSpeed(racer: RacerMotion): number {
  return RACE_CRUISE_SPEED + (racer.boostRemainingMs > 0 ? racer.boostSpeed : 0);
}
